import React, { useState, useEffect, useRef } from "react";
import { MessageList, Input, Button } from "react-chat-elements";
import "react-chat-elements/dist/main.css";
import "../styles/chatui.css";
import axios from "axios";
import { FaDownload } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useSelector } from "react-redux";
import { faSyncAlt, faSearch } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../shared/helper/LoadingSpinner";

const ChatUI = ({ selectedChat }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const chatRef = useRef(null);
  const { accessToken } = useSelector((state) => state?.auth);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const fetchMessages = async (phone_number) => {
    try {
      const response = await axios.get(apiUrl + "whatsapp/received-message", {
        params: { phone_number: selectedChat.phone_number },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const fetchedMessages = response.data.map((msg) => {
        const attachmentData = msg.attachment_data
          ? typeof msg.attachment_data === "string"
            ? JSON.parse(msg.attachment_data)
            : msg.attachment_data
          : null;

        const mimeType = attachmentData?.mime_type || attachmentData?.mimetype;
        const mediaType = mimeType?.split("/")[1]?.toUpperCase()
          || attachmentData?.filename?.split(".").pop()?.toUpperCase()
          || "FILE";
        const hasAttachment = Boolean(msg.attachment_url || attachmentData?.id);
        const filename = filenameFromUrl(msg.attachment_url)
          || attachmentData?.filename
          || attachmentData?.caption
          || `Attachment-${msg.id}`;

        return {
          id: msg.id,
          position: msg.from === selectedChat.phone_number ? "left" : "right",
          type: "text",
          text: msg.message,
          date: new Date(msg.created_at),
          attachment_data: attachmentData,
          attachment_url: msg.attachment_url,
          has_attachment: hasAttachment,
          media_type: mediaType,
          file_name: filename,
          from: msg.from,
        };
      });

      setMessages(fetchedMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const filenameFromUrl = (url) => {
    if (!url) return "";
    const parts = url.split("/");
    return parts[parts.length - 1];
  };

  const refreshMessages = async (phone_number) => {
    setIsRefreshing(true);
    await fetchMessages(phone_number);
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (phone_number) => {
    if (inputValue.trim()) {
      const newMessage = {
        position: "right",
        type: "text",
        text: inputValue,
        date: new Date(),
      };

      setMessages([...messages, newMessage]);

      try {
        await axios.post(
          apiUrl + "whatsapp/messages",
          {
            to: phone_number,
            text: inputValue,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        setInputValue("");
      } catch (error) {
        console.error("Error sending message:", error);
      }
    }
  };

  const handleDownloadAttachment = async (messageId, messageFrom) => {
    try {
      const response = await axios.get(apiUrl + "whatsapp/download-attachment", {
        params: { 
          message_id: messageId
         },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: "blob",
      });

      let fileName = messageFrom;
      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          fileName = match[1];
        }
      }

      const blob = new Blob([response.data], { type: response.data.type });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  const handlePreviewAttachment = async (messageId, mimeType) => {
    try {
      const response = await axios.get(
        `${apiUrl}whatsapp/download-attachment`,
        {
          params: { message_id: messageId },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: response.data.type });
      const previewUrl = URL.createObjectURL(blob);
      setPreviewUrl(previewUrl);
      setPreviewType(mimeType);
    } catch (error) {
      console.error("Error previewing file:", error);
      alert("Failed to preview file.");
    }
  };

  const filteredMessages = messages.filter((msg) => {
    if (!searchTerm) return true;
    const text = typeof msg.text === "string" ? msg.text : JSON.stringify(msg.text);
    return text.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setShowSearch(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    fetchMessages(selectedChat.phone_number);
    const interval = setInterval(fetchMessages, 60000);
    return () => clearInterval(interval);
  }, [selectedChat]);

  const onBack = () => {
    navigate(0);
  };

  return (
    <div className="chat-ui-container">
      <div className="chat-ui-header">
        <div className="d-flex align-items-center gap-2">
          {location.pathname === "/auth/chats" && (
            <FontAwesomeIcon
              icon={faArrowLeft}
              onClick={onBack}
              style={{
                color: "white",
                width: "16px",
                cursor: "pointer",
              }}
            />
          )}
          <span
            style={{
              color: "white",
              fontSize: "17px",
              fontWeight: "bold",
            }}
          >
            {selectedChat?.name}
          </span>
        </div>
        <div className="d-flex justify-content-between align-items-center px-2">
          <div className="chat-actions">
            <FontAwesomeIcon
              icon={faSearch}
              onClick={() => setShowSearch((prev) => !prev)}
              style={{ color: "white", cursor: "pointer", fontSize: "16px", marginRight: "20px" }}
              title="Search"
            />

            {isRefreshing ? (
              <LoadingSpinner />
            ) : (
              <FontAwesomeIcon
                icon={faSyncAlt}
                onClick={() => {
                  if (selectedChat?.phone_number) {
                    refreshMessages(selectedChat.phone_number);
                  }
                }}
                style={{
                  color: "white",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
                title="Refresh"
              />
            )}

          </div>
        </div>
      </div>

      <div className="chat-ui-messages" ref={chatRef}>

        {previewUrl && (
          <div className="preview-container">
            <div className="preview-header">
              <span>{previewType?.toUpperCase()} Preview</span>
              <button
                onClick={() => {
                  setPreviewUrl(null);
                  setPreviewType(null);
                }}
                className="close-preview"
              >
                ✖
              </button>
            </div>
            <iframe
              src={previewUrl}
              title="Preview"
              className="preview-iframe"
            ></iframe>
          </div>
        )}

        {showSearch && (
          <div
            className="chat-search-box"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "white",
              padding: "10px",
              borderBottom: "1px solid #ddd",
            }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
        )}

        <MessageList
          className="message-list"
          containerStyle={{
            paddingBottom: "15px",
            paddingLeft: "10px",
            paddingRight: "10px",
          }}
          dataSource={(() => {
            let groupedMessages = {};

            filteredMessages.forEach((msg) => {
              const msgDate = moment(msg.date).startOf("day");
              const today = moment().startOf("day");
              const yesterday = moment().subtract(1, "day").startOf("day");

              let dateLabel = msgDate.isSame(today)
                ? "Today"
                : msgDate.isSame(yesterday)
                ? "Yesterday"
                : msgDate.format("DD MMM YYYY");
              if (!groupedMessages[dateLabel]) {
                groupedMessages[dateLabel] = [];
              }
              groupedMessages[dateLabel].push(msg);
            });

            let formattedMessages = [];

            Object.keys(groupedMessages).forEach((dateLabel) => {
              formattedMessages.push({
                type: "system",
                text: <div className="seperator">{dateLabel}</div>,
              });

              groupedMessages[dateLabel].forEach((msg) => {
                formattedMessages.push({
                  position: msg.position,
                  type: "text",
                  text: (
                    <div className="message-item">
                      {(() => {
                          if (typeof msg.text === "string") {
                            try {
                              const parsed = JSON.parse(msg.text);
                              if (typeof parsed === "object" && parsed !== null) {
                                return Object.entries(parsed).map(([key, value], index) => (
                                  <div key={index}>
                                    <strong>{key}</strong>: {String(value)}
                                  </div>
                                ));
                              } else {
                                return msg.text;
                              }
                            } catch (e) {
                              // Not JSON, render string with line breaks
                              return msg.text.split("\n").map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ));
                            }
                          } else if (typeof msg.text === "object" && msg.text !== null) {
                            return Object.entries(msg.text).map(([key, value], index) => (
                              <div key={index}>
                                <strong>{key}</strong>: {String(value)}
                              </div>
                            ));
                          } else {
                            return "No message";
                          }
                        })()
                      }
                      {msg.has_attachment && (
                        <>
                         <br />
                          <div className="attachment-wrapper">
                            <span className="badge">{msg.media_type}</span>
                            <span
                              title={msg.file_name}
                              className="file-name"
                              onClick={() => handlePreviewAttachment(msg.id, msg.media_type)}
                            >
                              {msg.file_name || "View Attachment"}
                            </span>
                            <FaDownload
                              className="download-icon"
                              onClick={() => handleDownloadAttachment(msg.id, msg.from)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ),
                  date: msg.date,
                  dateString: moment(msg.date).format("DD/MM/YY hh:mm A"),
                });
              });
            });
            return formattedMessages;
          })()}
        />
      </div>

      <div className="chat-ui-input">
        <Input
          placeholder="Type your message..."
          multiline={false}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) =>
            e.key === "Enter" && handleSendMessage(selectedChat?.phone_number)
          }
          rightButtons={
            <Button
              color="white"
              backgroundColor="#075E54"
              text="Send"
              onClick={() => handleSendMessage(selectedChat?.phone_number)}
            />
          }
        />
      </div>
    </div>
  );
};

export default ChatUI;
